import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Role } from '../types/auth';

const RouteDiagnostic: React.FC = () => {
  const {
    user,
    loading,
    error,
    userRoles,
    hasRole,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md my-10">
      <h1 className="text-2xl font-bold mb-6">Route & Auth Diagnostic</h1>
      
      <div className="mb-8 p-4 bg-gray-50 rounded border">
        <h2 className="text-xl font-semibold mb-2">Current Location</h2>
        <pre className="bg-gray-100 p-2 rounded"><code>{JSON.stringify(location, null, 2)}</code></pre>
      </div>
      
      <div className="mb-8 p-4 bg-gray-50 rounded border">
        <h2 className="text-xl font-semibold mb-2">Authentication State (from AuthContext)</h2>
        <p className="mb-2"><strong>Loading:</strong> <span className={loading ? 'text-orange-500' : 'text-green-500'}>{loading ? 'Yes' : 'No'}</span></p>
        <p className="mb-2"><strong>`user` object:</strong> {user ? 'Authenticated' : 'Not authenticated'}</p>

        {error && (
          <div className="mt-2 p-3 bg-red-50 text-red-700 rounded">
            <h3 className="font-bold">Auth Error:</h3>
            <p>{error.message}</p>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 rounded">
          <h3 className="font-bold mb-2">User Details:</h3>
          {user ? (
            <>
              <p><strong>UID:</strong> {user.uid}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Display Name:</strong> {user.displayName || '(not set)'}</p>
            </>
          ) : (
            <p>No user logged in.</p>
          )}
        </div>
        
        <div className="mt-4 p-3 bg-green-50 rounded">
            <h3 className="font-bold mb-2">Roles:</h3>
            <p><strong>`userRoles` array:</strong> [{userRoles.join(', ')}]</p>
            <div className="mt-2">
                <h4 className="font-semibold">Role Checks (`hasRole`):</h4>
                <p>Is Admin: {hasRole('admin') ? 'Yes' : 'No'}</p>
                <p>Is Organizer: {hasRole('organizer') ? 'Yes' : 'No'}</p>
                <p>Is User: {hasRole('user') ? 'Yes' : 'No'}</p>
            </div>
        </div>
      </div>
      
      <div className="mb-8 p-4 bg-gray-50 rounded border">
        <h2 className="text-xl font-semibold mb-2">Common Routes</h2>
        <div className="grid grid-cols-2 gap-2">
          <Link to="/" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Home</Link>
          <Link to="/login" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Login</Link>
          <Link to="/register" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Register</Link>
          <Link to="/events" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Events</Link>
          <Link to="/profile" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Profile</Link>
          <Link to="/dashboard" className="px-4 py-2 bg-blue-100 rounded hover:bg-blue-200 transition">Dashboard</Link>
        </div>
      </div>
      
      <div className="mb-8 p-4 bg-gray-50 rounded border">
        <h2 className="text-xl font-semibold mb-2">Navigation Controls</h2>
        <button 
          onClick={() => navigate(-1)} 
          className="mr-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 transition"
        >
          Go Back
        </button>
        <button 
          onClick={() => window.location.reload()}
          className="mr-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 transition"
        >
          Reload Page
        </button>
        <button 
          onClick={() => navigate('/', { replace: true })}
          className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 transition"
        >
          Go Home (Replace)
        </button>
      </div>
      
      <div className="mb-8 p-4 bg-red-50 rounded border border-red-200">
        <h2 className="text-xl font-semibold mb-2 text-red-700">Troubleshooting</h2>
        <p className="text-red-700 mb-2">If you're experiencing navigation issues:</p>
        <ol className="list-decimal list-inside text-red-700">
          <li>Check if the routes are correctly configured in the router</li>
          <li>Verify that protected routes have the correct authentication checks</li>
          <li>Confirm that the AuthProvider is properly set up</li>
          <li>Check browser console for any errors</li>
          <li>Clear browser cache and cookies if needed</li>
        </ol>
      </div>
    </div>
  );
};

export default RouteDiagnostic; 